import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Z_INDEX } from "@/utils/z-index";

export interface TooltipProps {
  content?: React.ReactNode;
  /** Alias for content (Material UI compatibility) */
  title?: React.ReactNode;
  children: React.ReactElement;
  position?: "top" | "bottom" | "left" | "right";
  delay?: number;
  className?: string;
}

/**
 * Arc-style Tooltip component
 * 
 * Features:
 * - Multiple positions: top, bottom, left, right
 * - Configurable delay
 * - Smooth fade animation
 * - Auto-positioning with viewport awareness
 * - Portal-based rendering (works in modals with overflow)
 */
const Tooltip: React.FC<TooltipProps> = ({
  content,
  title,
  children,
  position = "top",
  delay = 200,
  className = "",
}) => {
  // Support both content and title (alias)
  const tooltipContent = content ?? title;
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const gap = 8; // Gap between trigger and tooltip

    let top = 0;
    let left = 0;

    switch (position) {
      case "top":
        top = triggerRect.top - tooltipRect.height - gap;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + gap;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case "left":
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - gap;
        break;
      case "right":
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + gap;
        break;
    }

    // Constrain to viewport
    const padding = 8;
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipRect.width - padding));
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipRect.height - padding));

    setTooltipPosition({ top, left });
  }, [position]);

  const showTooltip = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Update position when tooltip becomes visible
  useEffect(() => {
    if (isVisible) {
      requestAnimationFrame(updatePosition);
    }
  }, [isVisible, updatePosition]);

  // Animation variants
  const variants = {
    top: {
      initial: { opacity: 0, y: 5 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: 5 },
    },
    bottom: {
      initial: { opacity: 0, y: -5 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -5 },
    },
    left: {
      initial: { opacity: 0, x: 5 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: 5 },
    },
    right: {
      initial: { opacity: 0, x: -5 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -5 },
    },
  };

  // Arrow position styles (relative to tooltip)
  const arrowStyles = {
    top: "left-1/2 -translate-x-1/2 -bottom-1 border-t-content-primary border-x-transparent border-b-transparent",
    bottom: "left-1/2 -translate-x-1/2 -top-1 border-b-content-primary border-x-transparent border-t-transparent",
    left: "top-1/2 -translate-y-1/2 -right-1 border-l-content-primary border-y-transparent border-r-transparent",
    right: "top-1/2 -translate-y-1/2 -left-1 border-r-content-primary border-y-transparent border-l-transparent",
  };

  const tooltipElement = (
    <AnimatePresence>
      {isVisible && tooltipContent && (
        <motion.div
          ref={tooltipRef}
          role="tooltip"
          initial={variants[position].initial}
          animate={variants[position].animate}
          exit={variants[position].exit}
          transition={{ duration: 0.15 }}
          className={`fixed pointer-events-none ${className}`}
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            zIndex: Z_INDEX.POPOVER_IN_MODAL,
          }}
        >
          <div
            className="
              relative
              px-3 py-1.5
              text-xs font-medium
              text-content-inverse
              bg-content-primary
              rounded-arc-sm
              shadow-arc-lg
              whitespace-nowrap
            "
          >
            {tooltipContent}
            {/* Arrow */}
            <div
              className={`
                absolute w-0 h-0
                border-4
                ${arrowStyles[position]}
              `}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div
      ref={triggerRef}
      className="inline-flex"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}
      {typeof window !== "undefined" && createPortal(tooltipElement, document.body)}
    </div>
  );
};

Tooltip.displayName = "Tooltip";

export default Tooltip;









